import { Stack } from '@mui/material';

import { MAXIMUM_ROW_COUNT } from '@/components/tables/DataTable/constants';
import { usePaginatedDataTableQuery } from '@/components/tables/DataTable/hooks/usePaginatedDataTableQuery';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useLiabilitiesTableQuery } from '@/modules/liabilities/LiabilitiesTable/graphql/LiabilitiesTable.generated';
import { LiabilitiesTable } from '@/modules/liabilities/LiabilitiesTable/LiabilitiesTable';
import { DownloadLiabilitiesCSVButton } from '@/pages/ClientDetailsPage/ClientDetailsEntitiesPage/ClientDetailsManageLiabilitiesPage/DownloadLiabilitiesCSVButton/DownloadLiabilitiesCSVButton';
import { LiabilityOrderField, OrderDirection } from '@/types/schema';

import { EntitiesBreadcrumb } from '../components/EntitiesBreadcrumb';

interface ClientDetailsManageLiabilitiesPageInnerProps {
  householdId: string;
}

function ClientDetailsManageLiabilitiesPageInner({
  householdId,
}: ClientDetailsManageLiabilitiesPageInnerProps) {
  const { reportError } = useReportError();
  const [paginatedTableProps, { data, refetch: updateTable, loading }] =
    usePaginatedDataTableQuery(useLiabilitiesTableQuery, {
      pageSize: MAXIMUM_ROW_COUNT,
      variables: {
        where: {
          hasHouseholdWith: [
            {
              id: householdId,
            },
          ],
        },
        orderBy: [
          {
            field: LiabilityOrderField.UpdatedAt,
            direction: OrderDirection.Desc,
          },
        ],
      },
      onError: (error) => {
        reportError('failed to load liabilities', error);
      },
    });

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <EntitiesBreadcrumb pageCrumb="Liabilities" householdId={householdId} />
        <DownloadLiabilitiesCSVButton
          householdId={householdId}
          disabled={!data || data.length === 0}
        />
      </Stack>
      <LiabilitiesTable
        householdId={householdId}
        tableProps={{
          ...paginatedTableProps,
          hideFooterPagination: true,
          pageSize: MAXIMUM_ROW_COUNT,
        }}
        data={data}
        updateTable={updateTable}
        loading={loading}
      />
    </Stack>
  );
}

export function ClientDetailsManageLiabilitiesPage() {
  const householdId = useRequiredParam('householdId');

  return <ClientDetailsManageLiabilitiesPageInner householdId={householdId} />;
}
